import { Button, Flex, MantineStyleProps, Stack, useMatches } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal, ResponsivePageContainer } from "@/components/elements";
import PlansCard from "@/components/elements/PlansCard/PlansCard";
import { getPlanCards, submitPayment } from "@/components/elements/PlansCard/functions";
import { useUser } from "@/services/auth";
import { useCreatePayment } from "@/services/payments/create-payment";
import { usePlansUser } from "@/services/plans/get-user-plan";
import { showCustomNotification } from "@/utils/notifications";

import type { billingPeriodType } from "@/components/elements/PlansCard";
import type { PlanCard, PlanName } from "@/types/plans";
import type { FC } from "react";

type ChoosePlanModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
} & MantineStyleProps;

export const ChoosePlanModal: FC<ChoosePlanModalProps> = ({ isOpen, onClose, title, ...props }) => {
  const { t } = useTranslation();
  const { data: user } = useUser();

  const [billingPeriod] = useState<billingPeriodType>("Monthly");
  const { data: plan } = usePlansUser();
  const [selectedPlan, setSelectedPlan] = useLocalStorage<PlanName | null>({
    key: "selectedPlan",
    defaultValue: plan?.name ?? null,
  });

  // Keep the local selected plan in sync with the user's actual plan
  useEffect(() => {
    setSelectedPlan(plan?.name ?? null);
  }, [plan?.name, setSelectedPlan]);

  const isMobile = useMatches({ base: true, sm: false });
  const cardHeight = isMobile ? "auto" : "33em";

  const cards = getPlanCards(t);

  const createPaymentMutation = useCreatePayment({
    mutationConfig: {
      onSuccess: (data) => {
        // If user selected the free "starter" plan, avoid navigating away — backend will update the plan
        if (selectedPlan === "starter") {
          return;
        }

        // For paid plans, open the checkout in a new tab instead of forcing a full navigation
        if (data.url) {
          window.location.href = data.url;
        }
      },
      onError: () => {
        showCustomNotification({
          title: t("payments.errors.generic_error"),
          message: t("payments.errors.generic_error_description"),
          autoClose: 6000,
        });
      },
    },
  });

  const submitPlan = submitPayment(selectedPlan, user, createPaymentMutation, plan, true);

  const handleSubmit = () => {
    try {
      if (typeof window !== "undefined" && window.sessionStorage) {
        window.sessionStorage.setItem("forcePlanToLoad", String(Date.now() + 3000));
        window.dispatchEvent(new Event("forcePlanToLoadChanged"));

        // Clear the flag automatically after 3 seconds
        setTimeout(() => {
          try {
            window.sessionStorage.removeItem("forcePlanToLoad");
            window.dispatchEvent(new Event("forcePlanToLoadChanged"));
          } catch {
            // ignore errors
          }
        }, 3000);
      }
    } catch {
      // sessionStorage might not be available. In this case, ignore the error
    }

    submitPlan();

    setTimeout(() => {
      onClose();
    }, 500);
  };

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={title}
      centered
      size={isMobile ? "100%" : 1150}
      {...props}
    >
      <ResponsivePageContainer p="sm">
        <Stack gap="md" flex={1}>
          <PlansCard.Grid>
            {cards?.map((card: PlanCard, index: number) => (
              <PlansCard.Col index={index} card={card} key={card.title}>
                <PlansCard
                  h={cardHeight}
                  selectedCard={card.plan || "starter"}
                  selectedPlan={selectedPlan}
                  onClick={() => setSelectedPlan(card.plan)}
                >
                  <PlansCard.Header title={card.title} description={card.description} />
                  <PlansCard.Content list={card.list} title={card.title} />
                  <PlansCard.Footer billingPeriod={billingPeriod} price={card.price} />
                </PlansCard>
              </PlansCard.Col>
            ))}
          </PlansCard.Grid>

          <Flex
            justify="center"
            w={{ base: "100%", md: 500 }}
            gap="lg"
            mx="auto"
            pt="xl"
            pb="sm"
            maw="100%"
          >
            <Button
              variant="outline"
              w="50%"
              maw="100%"
              onClick={onClose}
              aria-label={t("common.cancel")}
            >
              {t("common.cancel")}
            </Button>
            <Button
              w="50%"
              maw="100%"
              loading={false} // TODO: add loading state
              onClick={handleSubmit}
              /* aria-label={isUpdatingPlan ? t("plans.saving") : t("plans.choosePlan")} */ // TODO: uncomment when loading state is added
            >
              {/*   {isUpdatingPlan ? t("plans.saving") : TODO: uncomment when loading state is added  */}
              {t("plans.choosePlan")}
            </Button>
          </Flex>
        </Stack>
      </ResponsivePageContainer>
    </Modal>
  );
};
